import { DefaultNumberInputField } from '~/common/components/defaultUIComponents/DefaultNumberInputField';
import { InputField } from '~/common/components/defaultUIComponents/InputField';
import { Heading } from '~/common/components/defaultUIComponents/Heading';
import { Button } from '~/common/components/defaultUIComponents/Button';
import EinmalAuftragApi from '~/common/api/zeiterfassungServerApi/EinmalAuftragApi';
import { Grid, GridItem, useToast } from '@chakra-ui/react';
import { useNavigate, useParams } from 'react-router-dom';
import routes from '~/common/constants/routes.json';
import React, { useMemo } from 'react';
import { Form, Formik } from 'formik';
import * as Yup from 'yup';

interface EinmalAuftragFormValues {
  name: string;
  description: string;
  durationMinutes: number;
  priority: number;
}

const EinmalAuftragFormSchema = Yup.object({
  name: Yup.string().required(),
  description: Yup.string().required(),
  durationMinutes: Yup.number().min(1).required(),
  priority: Yup.number().min(0).required(),
});

const EinmalAuftragForm: React.FC = () => {
  const navigate = useNavigate();
  const { id } = useParams<{ id?: string }>();

  const { data: einmalAuftrag } = EinmalAuftragApi.hooks.useEinmalAuftrag(
    { id },
    { enabled: !!id },
  );
  const { mutateAsync: createEinmalAuftrag } =
    EinmalAuftragApi.hooks.useCreateEinmalAuftrag();
  const { mutateAsync: updateEinmalAuftrag } =
    EinmalAuftragApi.hooks.useUpdateEinmalAuftrag();

  const toast = useToast({
    status: 'error',
    description: 'generic.error',
  });

  const initialValues = useMemo((): EinmalAuftragFormValues => {
    if (einmalAuftrag) {
      // duration comes as "HH:MM:SS" or ISO duration — parse as total minutes
      const durationMinutes = parseDurationToMinutes(einmalAuftrag.duration);
      return {
        name: einmalAuftrag.name,
        description: einmalAuftrag.description,
        durationMinutes,
        priority: einmalAuftrag.priority,
      };
    }
    return {
      name: '',
      description: '',
      durationMinutes: 0,
      priority: 0,
    };
  }, [einmalAuftrag]);

  const handleSubmit = (values: EinmalAuftragFormValues) => {
    const durationSeconds = values.durationMinutes * 60;
    if (id) {
      updateEinmalAuftrag({
        id,
        name: values.name,
        description: values.description,
        duration: durationSeconds,
        priority: values.priority,
      })
        .then(() => navigate(routes.EINMAL_AUFTRAEGE))
        .catch(() => toast({}));
    } else {
      createEinmalAuftrag({
        name: values.name,
        description: values.description,
        duration: durationSeconds,
        priority: values.priority,
      })
        .then(() => navigate(routes.EINMAL_AUFTRAEGE))
        .catch(() => toast({}));
    }
  };

  return (
    <>
      {!id ? (
        <Heading>einmalAuftraege.heading</Heading>
      ) : (
        <Heading>einmalAuftraege.heading</Heading>
      )}
      <Formik
        initialValues={initialValues}
        onSubmit={handleSubmit}
        validationSchema={EinmalAuftragFormSchema}
        validateOnChange
        enableReinitialize
      >
        {() => (
          <Form>
            <Grid templateColumns="repeat(12, 1fr)" gap={5}>
              <GridItem colSpan={12}>
                <InputField
                  name="name"
                  label="einmalAuftraege.name.label"
                  placeholder=""
                  mt={1}
                />
              </GridItem>
              <GridItem colSpan={12}>
                <InputField
                  name="description"
                  label="einmalAuftraege.description.label"
                  placeholder=""
                  mb={5}
                  textarea
                />
              </GridItem>
              <GridItem colSpan={6}>
                <DefaultNumberInputField
                  name="durationMinutes"
                  label="einmalAuftraege.duration.label"
                  min={0}
                />
              </GridItem>
              <GridItem colSpan={6}>
                <DefaultNumberInputField
                  name="priority"
                  label="einmalAuftraege.priority.label"
                  min={0}
                />
              </GridItem>
              <GridItem colSpan={6}>
                <Button
                  w="full"
                  type="button"
                  onClick={() => navigate(routes.EINMAL_AUFTRAEGE)}
                >
                  einmalAuftraege.form.cancel
                </Button>
              </GridItem>
              <GridItem colSpan={6}>
                <Button w="full" type="submit">
                  einmalAuftraege.form.submit
                </Button>
              </GridItem>
            </Grid>
          </Form>
        )}
      </Formik>
    </>
  );
};

function parseDurationToMinutes(duration: string): number {
  if (!duration) return 0;
  // Handles "HH:MM:SS" format
  const parts = duration.split(':');
  if (parts.length === 3) {
    const hours = parseInt(parts[0] ?? '0', 10);
    const minutes = parseInt(parts[1] ?? '0', 10);
    return hours * 60 + minutes;
  }
  return 0;
}

export default EinmalAuftragForm;
